// 새 형식 장면(v2 샘플 12개). 초안 원본: outputs/content-drafts/scenes-v2-samples.json, 설계: docs/event-system-v2-design.md
// 한 장면 = 상황 한 문장 + 선택지 3개(안정 / 도전 / 회피) + 사주 변형(natal) + 돈 효과(money).
// 금액은 연 단위(만원). {…} 는 lib/scenes.js 가 경제 상태에서 채운다. 배율·확률은 임시값이다(코호트 측정 전).
const TRAIT = { stable: '안정', challenge: '열정', avoid: '신중' };
// 화면 이름 → 엔진 영역 키. 데이터에는 화면 이름으로 쓰고 여기서 한 번에 바꾼다.
export const GROUP_OF = { 관계: '비겁', 활동: '식상', 재물: '재성', 직업: '관성', 학업: '인성' };
const keyed = (obj) => Object.fromEntries(Object.entries(obj ?? {}).map(([k, v]) => [GROUP_OF[k] ?? k, v]));

// slot: stable(안정) / challenge(도전) / avoid(회피). 도전은 행동(act), 나머지는 다른 방법(alternative), 효과·위험이 없는 회피는 하지 않기(decline)
function choice(slot, label, hint, effects, risk, fail, money) {
  const kind = slot === 'challenge' ? 'act' : slot === 'avoid' && !Object.keys(effects).length && !risk && !money ? 'decline' : 'alternative';
  effects = keyed(effects); fail = keyed(fail);
  const target = Object.keys(effects)[0] ?? null;
  return { slot, kind, label, hint, trait: TRAIT[slot], targetField: target, delta: target ? effects[target] : 0, risk, effects,
    failureEffects: fail ?? {}, timeCost: 0, ...(money ? { money } : {}),
    successText: kind === 'decline' ? '이번에는 하지 않았다.' : '선택대로 진행해 마무리했다.',
    failureText: '시도했지만 뜻대로 되지 않았다.' };
}
const E = (slot, label, hint, effects, risk = 0, fail, money) => choice(slot, label, hint, effects, risk, fail, money);
const scene = (id, field, title, age, situation, choices, extra = {}) => ({ id, category: 'scene', field: GROUP_OF[field] ?? field, title, description: situation,
  ageMin: age[0], ageMax: age[1], baseWeight: 1, cooldownYears: 99, contentVersion: 1, rules: { once: true }, rate: 0.4, natal: [], requires: [], ...extra, choices });

const withEdu = (sceneId, slot, edu, list) => list.map((sc) => (sc.id === sceneId ? { ...sc, choices: sc.choices.map((c) => (c.slot === slot ? { ...c, edu } : c)) } : sc));

const BASE_SCENES = [
  scene('s_group_project', '직업', '조별과제 마감이 이틀 남았다', [15, 23], '팀원 4명 중 PPT 담당이 아직 없다.', [
    E('stable', '내가 맡는다', '직업↑ 시간↓', { 직업: 3 }, 0.15, { 직업: -1 }),
    E('avoid', '다른 사람에게 미룬다', '관계↓ 시간 여유', { 관계: -1 }),
    E('challenge', '역할을 나눠 제안한다', '관계↑ 직업↑ 조금 느림', { 관계: 2, 직업: 2 }, 0.25, { 관계: -1 }),
  ], { rate: 0.5, natal: [
    { when: { open: '직업' }, slot: 'stable', rewardScale: 1.3, note: "직업 기회가 열린 해라 '내가 맡는다'의 보상이 커졌다." },
    { when: { weak: '관계' }, slot: 'challenge', riskDelta: -0.1, note: "관계 영역이 약해 '역할을 나눠 제안한다'를 조심스럽게 시도할 수 있다." },
  ] }),
  scene('s_track_choice', '학업', '진로를 정해야 한다', [16, 17], '문과, 이과, 특성화고 중 하나를 고를 때가 왔다.', [
    E('stable', '성적이 좋은 쪽으로 간다', '학업↑', { 학업: 3 }, 0.1, { 학업: -1 }),
    E('challenge', '하고 싶은 쪽으로 간다', '활동↑ 학업은 불확실', { 활동: 3 }, 0.3, { 활동: -1, 학업: -1 }),
    E('avoid', '특성화고에서 일찍 일한다', '직업↑ 학업↓', { 직업: 2, 학업: -1 }, 0),
  ], { rate: 0.8, natal: [
    { when: { strong: '학업' }, slot: 'stable', rewardScale: 1.2, note: "학업 영역이 강해 '성적이 좋은 쪽으로 간다'의 보상이 커졌다." },
    { when: { strong: '활동' }, slot: 'challenge', riskDelta: -0.1, note: "활동 영역이 강해 '하고 싶은 쪽으로 간다'의 실패 확률이 낮아졌다." },
  ] }),
  scene('s_exam', '학업', '입시 결과가 나왔다', [18, 19], '원하던 학교는 떨어졌고, 한 곳은 합격했다.', [
    E('stable', '합격한 곳에 간다', '학업↑ 바로 진행', { 학업: 2 }, 0.05),
    E('challenge', '재수한다', '학업↑↑ 아니면 ↓ · 첫 직장이 1년 늦어짐', { 학업: 4 }, 0.45, { 학업: -2 }),
    E('avoid', '취업으로 간다', '직업↑ 학업↓', { 직업: 2, 학업: -1 }, 0),
  ], { rate: 0.8, natal: [
    { when: { strong: '학업' }, slot: 'challenge', riskDelta: -0.15, note: "학업 영역이 강해 '재수한다'의 실패 확률이 낮아졌다." },
    { when: { transition: '막는' }, slot: 'challenge', riskDelta: 0.1, note: "막히기 쉬운 대운 시기라 '재수한다'의 실패 확률이 높아졌다." },
  ] }),
  scene('s_first_income', '재물', '첫 소득이 발생했다', [24, 26], '{직종} 직종, 첫해 연 소득 {firstIncome}만원(세전)이 정해졌다.', [
    E('stable', '저축한다', '순자산↑', { 재물: 2 }, 0, undefined, { always: { net: { of: 'income', pct: 0.3 } } }),
    E('challenge', '자기계발에 쓴다', '학업↑ 순자산↓', { 학업: 3 }, 0.15, { 학업: 1 }, { always: { net: { of: 'income', pct: -0.15 } } }),
    E('avoid', '부모님께 드린다', '관계↑ 순자산↓', { 관계: 3 }, 0, undefined, { always: { net: { of: 'income', pct: -0.1 } } }),
  ], { rate: 1, requires: ['working'], natal: [
    { when: { weak: '재물' }, slot: 'stable', rewardScale: 1.2, note: '재물 영역이 약해 저축의 효과가 더 크게 느껴진다.' },
  ] }),
  scene('s_romance', '관계', '마음에 드는 사람이 고백했다', [20, 34], '같은 모임에서 알게 된 사람이 사귀자고 한다.', [
    E('stable', '친구로 지낸다', '관계 유지', { 관계: 1 }),
    E('challenge', '사귄다', '관계↑ 시간↓', { 관계: 3 }, 0.25, { 관계: -2 }),
    E('avoid', '거절한다', '시간 여유', {}),
  ], { rate: 0.35, natal: [
    { when: { palaceHap: true }, slot: 'challenge', riskDelta: -0.15, note: "배우자 자리(일지)가 그해 운과 합을 이뤄 '사귄다'의 실패 확률이 낮아졌다." },
  ] }),
  scene('s_job_offer', '직업', '다른 회사에서 연봉 {offer}만원을 제안했다', [27, 50], '지금 연 소득은 {income}만원이다. 새 회사는 사람이 낯설다.', [
    E('stable', '남는다', '안정', { 직업: 1 }),
    E('challenge', '옮긴다', '소득↑ 관계↓ 적응 1년', { 직업: 3, 관계: -1 }, 0.25, { 직업: -2 }, { success: { incomeMod: { pct: 0.1, years: 99 } } }),
    E('avoid', '지금 회사에 조건을 요구한다', '성공하면 소득↑, 아니면 관계↓', { 직업: 2 }, 0.4, { 관계: -2 }, { success: { incomeMod: { pct: 0.05, years: 99 } } }),
  ], { rate: 0.3, cooldownYears: 8, rules: {}, requires: ['working'], natal: [
    { when: { transition: '바꾸는' }, slot: 'challenge', riskDelta: -0.15, note: "방향을 다시 정하는 대운 시기라 '옮긴다'의 실패 확률이 낮아졌다." },
    { when: { yong: '역용' }, slot: 'challenge', rewardScale: 1.3, lossScale: 1.3, note: "역용 격국이라 '옮긴다'는 보상도 손실도 커진다." },
  ] }),
  scene('s_startup', '재물', '친구가 같이 창업하자고 한다', [28, 45], '초기 자금으로 {stake}만원이 필요하다.', [
    E('stable', '투자만 한다', '작게 벌거나 잃는다', { 재물: 2 }, 0.4, { 재물: -1 }, { success: { net: { of: 'stake', pct: 0.5 } }, fail: { net: { of: 'stake', pct: -0.5 } } }),
    E('challenge', '합류한다', '크게 벌거나 크게 잃는다', { 직업: 4, 재물: 3 }, 0.55, { 재물: -3 }, { success: { net: { of: 'stake', pct: 3 } }, fail: { net: { of: 'stake', pct: -1 } } }),
    E('avoid', '거절한다', '변화 없음', {}),
  ], { rate: 0.3, requires: ['working'], natal: [
    { when: { strong: '재물' }, slot: 'challenge', riskDelta: -0.1, note: "재물 영역이 강해 '합류한다'의 실패 확률이 낮아졌다." },
    { when: { yong: '역용' }, slot: 'challenge', rewardScale: 1.3, lossScale: 1.3, note: "역용 격국이라 '합류한다'는 보상도 손실도 커진다." },
    { when: { transition: '막는' }, slot: 'challenge', riskDelta: 0.1, note: "막히기 쉬운 대운 시기라 '합류한다'의 실패 확률이 높아졌다." },
  ] }),
  scene('s_invest', '재물', '모은 돈 {cash}만원을 어떻게 둘까?', [26, 60], '예금, 주식, 그냥 쓰기 중에서 고른다.', [
    E('stable', '예금에 넣는다', '거의 안전 · 연 1.5%', { 재물: 1 }, 0, undefined, { always: { net: { of: 'cash', pct: 0.015 } } }),
    E('challenge', '주식에 투자한다', '폭락할 수도 · 평균 연 5%', { 재물: 3 }, 0.45, { 재물: -2 }, { success: { net: { of: 'cash', pct: 0.21 } }, fail: { net: { of: 'cash', pct: -0.14 } } }),
    E('avoid', '그냥 쓴다', '활동↑ 순자산↓', { 활동: 2 }, 0, undefined, { always: { net: { of: 'cash', pct: -0.2 } } }),
  ], { rate: 0.3, cooldownYears: 8, rules: {}, requires: ['cash'], stat: { text: '장기 실질수익률 대략 예금 1~2%, 주식 5%, 주택 3%', source: 'UBS Global Investment Returns Yearbook(한국 값은 조사 필요)' }, natal: [
    { when: { strong: '재물' }, slot: 'challenge', riskDelta: -0.05, note: "재물 영역이 강해 '주식에 투자한다'의 실패 확률이 조금 낮아졌다." },
  ] }),
  scene('s_parent_ill', '관계', '부모님이 아프시다', [40, 58], '병원에 다니셔야 해서 누군가 챙겨야 한다. 형제와 의견이 갈린다.', [
    E('stable', '요양 시설을 알아본다', '순자산↓ 시간 여유', { 재물: -1 }, 0.1, undefined, { always: { net: { of: 'income', pct: -0.25 } } }),
    E('challenge', '직접 돌본다', '관계↑ 소득↓ 1~2년', { 관계: 3, 직업: -2 }, 0.2, { 관계: -1 }, { always: { incomeMod: { pct: -0.3, years: 2 } } }),
    E('avoid', '형제에게 맡긴다', '관계↓', { 관계: -2 }),
  ], { rate: 0.3, natal: [
    { when: { weak: '관계' }, slot: 'challenge', riskDelta: 0.1, note: "관계 영역이 약해 '직접 돌본다'가 더 버겁다." },
  ] }),
  scene('s_early_retire', '직업', '회사가 명예퇴직을 받는다', [50, 58], '위로금 {bonus}만원이 나온다. 다음 일은 정해지지 않았다.', [
    E('stable', '남는다', '소득 유지', { 직업: 1 }),
    E('challenge', '받고 나가 다른 일을 찾는다', '순자산↑ 소득 공백', { 직업: -1, 활동: 2 }, 0.35, { 재물: -1 }, { always: { net: { of: 'bonus', pct: 1 }, incomeMod: { pct: -1, years: 2 } } }),
    E('avoid', '받고 쉰다', '순자산↑ 소득 없음', { 활동: 1 }, 0, undefined, { always: { net: { of: 'bonus', pct: 1 }, retire: true } }),
  ], { rate: 0.4, requires: ['working'], note: '위로금 {bonus}는 연 소득 ×1.5(게임 추정, 통계 조사 필요)', natal: [
    { when: { transition: '바꾸는' }, slot: 'challenge', riskDelta: -0.15, note: "방향을 다시 정하는 대운 시기라 '다른 일을 찾는다'의 실패 확률이 낮아졌다." },
  ] }),
  scene('s_pension', '재물', '연금을 언제부터 받을까?', [60, 65], '바로 받으면 65세 이후 연금이 줄고({early}만원 수준), 늦추면 65세부터 더 많이 받는다.', [
    E('stable', '바로 받는다', '65세 이후 연금이 30% 줄어듦(조기 수령)', { 재물: 1 }, 0, undefined, { always: { pension: { start: 'now', mult: 0.7 } } }),
    E('challenge', '늦춰서 더 받는다', '65세 이후 연금 +36%(늦춘 만큼)', { 재물: 2 }, 0.2, { 재물: -1 }, { always: { pension: { start: 65, mult: 1.36 } } }),
    E('avoid', '일을 더 한다', '65세까지 소득 유지', { 직업: 2 }, 0.1, undefined, { always: { workUntil: 65, pension: { start: 65, mult: 1 } } }),
  ], { rate: 0.7, stat: { text: '65세 이상 가구주 가구소득 중앙값 연 3,198만원(60세 이상은 3,978만원)', source: '가계금융복지조사 2025, KOSIS DT_1HDAAB02' }, note: '감액(×0.7)과 +36%는 게임 추정(국민연금 제도 값은 확인하지 못함)' }),
  scene('s_retired_days', '활동', '은퇴 후 하루를 어떻게 보낼까?', [61, 75], '일이 없어진 첫 해다.', [
    E('stable', '손주를 돌본다', '관계↑', { 관계: 3 }, 0.05),
    E('challenge', '배우고 싶던 것을 시작한다', '학업↑ 활동↑', { 학업: 2, 활동: 2 }, 0.15, { 활동: -1 }),
    E('avoid', '쉰다', '변화 없음', {}),
  ], { rate: 0.5, natal: [
    { when: { strong: '활동' }, slot: 'challenge', rewardScale: 1.2, note: "활동 영역이 강해 '배우고 싶던 것을 시작한다'의 보상이 커졌다." },
  ] }),
  // ───── 추가 장면: 군대·대학·육아·교육비·간병·퇴직 ─────
  scene('s_military', '직업', '입영 통지서가 나왔다', [20, 24], '병역 의무를 언제 어떻게 마칠지 정해야 한다.', [
    E('stable', '바로 입대한다', '직업↑ 관계↑ · 첫 직장이 2년 늦어짐', { 직업: 2, 관계: 1 }, 0.05),
    E('challenge', '장교·부사관에 지원한다', '성공하면 직업↑↑ · 떨어지면 일반 입대(첫 직장 2년 늦어짐)', { 직업: 3 }, 0.4, { 직업: -1 }),
    E('avoid', '학업을 마치고 가기로 미룬다', '학업↑ · 나중에 입대(첫 직장 2년 늦어짐)', { 학업: 1 }),
  ], { rate: 0.7, requires: ['male'], natal: [
    { when: { strong: '직업' }, slot: 'challenge', riskDelta: -0.15, note: "직업 영역이 강해 '장교·부사관에 지원한다'의 실패 확률이 낮아졌다." },
  ] }),
  scene('s_major', '학업', '전공이 나와 맞지 않는다', [19, 23], '학기를 몇 번 보내 보니 배우는 내용이 흥미롭지 않다.', [
    E('stable', '그대로 다닌다', '학업 유지', { 학업: 1 }),
    E('challenge', '전과를 알아본다', '학업↑ 아니면 한 학기 허비', { 학업: 3 }, 0.3, { 학업: -1 }),
    E('avoid', '휴학하고 일해 본다', '직업↑ 재물↑ 학업↓ · 졸업과 첫 직장이 1년 늦어짐', { 직업: 1, 재물: 1, 학업: -1 }),
  ], { rate: 0.5, requires: ['college'], natal: [
    { when: { strong: '활동' }, slot: 'challenge', riskDelta: -0.1, note: "활동 영역이 강해 '전과를 알아본다'가 더 잘 풀린다." },
  ] }),
  scene('s_childcare', '관계', '아이를 맡길 곳이 없다', [27, 44], '돌봄 공백이 생겼다. 육아휴직을 낼지, 맡길 곳을 구할지 정해야 한다.', [
    E('stable', '돌봄 서비스를 이용한다', '순자산↓ 일 유지', { 직업: 1 }, 0, undefined, { always: { net: { of: 'income', pct: -0.1 } } }),
    E('challenge', '육아휴직을 쓴다', '관계↑↑ 1년 소득↓', { 관계: 3, 직업: -1 }, 0.1, { 관계: 1 }, { always: { incomeMod: { pct: -0.4, years: 1 } } }),
    E('avoid', '부모님께 맡긴다', '관계 유지 · 비용 적음', { 관계: 1 }, 0.2, { 관계: -1 }),
  ], { rate: 0.7, requires: ['kidUnder6'], natal: [
    { when: { strong: '관계' }, slot: 'challenge', rewardScale: 1.2, note: "관계 영역이 강해 육아휴직의 보람이 커졌다." },
  ] }),
  scene('s_child_edu', '인성', '아이가 학원을 보내 달라고 한다', [36, 54], '교육비로 연 {eduCost}만원쯤 든다. 가구 평균 교육비 수준이다.', [
    E('stable', '아이의 선택에 맡긴다', '관계↑ 비용 적음', { 관계: 1 }),
    E('challenge', '학원을 보낸다', '관계↑ 순자산↓ 아이의 학업↑', { 관계: 1, 재물: -1 }, 0, undefined, { always: { net: { abs: -292 } } }),
    E('avoid', '집에서 직접 가르친다', '학업↑ 직업↓ 시간↓', { 학업: 1, 직업: -1 }, 0.2, { 관계: -1 }),
  ], { rate: 0.6, requires: ['kidSchool'], stat: { text: '가구 평균 교육비 연 292만원', source: '가계금융복지조사 2025, KOSIS DT_1HDAAB07' }, natal: [] }),
  scene('s_spouse_ill', '관계', '배우자가 아프다', [48, 72], '병원에 오래 다녀야 하고, 곁에서 챙길 사람이 필요하다.', [
    E('stable', '간병인을 쓴다', '순자산↓ 일 유지', { 재물: -1 }, 0.05, undefined, { always: { net: { of: 'income', pct: -0.2 } } }),
    E('challenge', '직접 돌본다', '관계↑↑ 소득↓ 2년', { 관계: 3, 직업: -2 }, 0.15, { 관계: 1 }, { always: { incomeMod: { pct: -0.25, years: 2 } } }),
    E('avoid', '가족과 번갈아 돌본다', '관계↑ 비용 나눔', { 관계: 1 }, 0.2, { 관계: -1 }),
  ], { rate: 0.4, requires: ['married'], natal: [
    { when: { weak: '관계' }, slot: 'challenge', riskDelta: 0.1, note: "관계 영역이 약해 '직접 돌본다'가 더 버겁다." },
  ] }),
  scene('s_midlife_health', '활동', '몸이 예전 같지 않다', [40, 56], '계단만 올라도 숨이 차다. 건강 관리를 해야 할 때가 왔다.', [
    E('stable', '건강검진을 받는다', '건강 확인 · 순자산 조금↓', { 활동: 1 }, 0, undefined, { always: { net: { abs: -30 } } }),
    E('challenge', '운동을 시작한다', '활동↑↑ 시간↓', { 활동: 3 }, 0.3, { 활동: 0 }, { always: { net: { abs: -100 } } }),
    E('avoid', '그냥 지낸다', '변화 없음', {}),
  ], { rate: 0.4, natal: [
    { when: { strong: '활동' }, slot: 'challenge', riskDelta: -0.15, note: "활동 영역이 강해 '운동을 시작한다'가 잘 이어진다." },
  ] }),
  scene('s_retire_plan', '재물', '정년이 5년 남았다', [55, 59], '지금 모은 돈으로 은퇴 뒤 생활을 어떻게 할지 떠올려 봐야 한다.', [
    E('stable', '지금 일을 이어간다', '직업 유지', { 직업: 1 }),
    E('challenge', '재취업을 준비한다', '학업↑ 활동↑ 공백 위험', { 학업: 2, 활동: 1 }, 0.3, { 학업: 1 }),
    E('avoid', '노후 자금을 점검한다', '재물↑', { 재물: 2 }),
  ], { rate: 0.7, requires: ['job'] }),
  scene('s_retire_day', '직업', '퇴직하는 날이 됐다', [60, 60], '정년이다. 같은 일을 이어갈지, 쉴지, 새 일을 해 볼지 정한다.', [
    E('stable', '같은 일을 시간제로 이어간다', '소득 절반 · 65세까지', { 직업: 1 }, 0, undefined, { always: { workUntil: 65, incomeMod: { pct: -0.5, years: 5 } } }),
    E('challenge', '새 일에 도전한다', '귀농·창업 · 크게 벌거나 잃는다', { 활동: 2, 재물: 2 }, 0.4, { 재물: -2 }, { success: { workUntil: 65, incomeMod: { pct: -0.3, years: 5 } }, fail: { net: { abs: -1500 } } }),
    E('avoid', '완전히 쉰다', '소득 없음 · 시간↑', { 활동: 1 }),
  ], { rate: 1, requires: ['job'], natal: [
    { when: { strong: '재물' }, slot: 'challenge', riskDelta: -0.1, note: "재물 영역이 강해 '새 일에 도전한다'의 실패 확률이 낮아졌다." },
  ] }),
];

// 학력: 진로 선택과 입시 결과로 정해진다. 재수는 성공하면 대학, 실패하면 고졸이다.
const withDelay = (sceneId, slot, delay, list) => list.map((sc) => (sc.id === sceneId ? { ...sc, choices: sc.choices.map((c) => (c.slot === slot ? { ...c, delay } : c)) } : sc));

const WITH_EDU = [
  ['s_track_choice', 'stable', 'college'], ['s_track_choice', 'challenge', 'college'], ['s_track_choice', 'avoid', 'vocational'],
  ['s_exam', 'stable', 'college'], ['s_exam', 'challenge', { success: 'college', fail: 'highschool' }], ['s_exam', 'avoid', 'highschool'],
].reduce((list, [id, slot, edu]) => withEdu(id, slot, edu, list), BASE_SCENES);

// 군 복무: 입대하면 첫 직장이 2년 늦어진다. 장교·부사관 지원에 성공하면 늦어지지 않고, 떨어지면 일반 입대다.
export const SCENES = [
  ['s_military', 'stable', 2], ['s_military', 'challenge', { success: 0, fail: 2 }], ['s_military', 'avoid', 2],
  ['s_major', 'avoid', 1], ['s_exam', 'challenge', 1],
].reduce((list, [id, slot, delay]) => withDelay(id, slot, delay, list), WITH_EDU);
